/**
 * Baby-Based Firestore Service Layer
 * Handles data under babies/{babyId}/feeds, etc.
 */

import {
  collection,
  doc,
  getDocs,
  addDoc,
  deleteDoc,
  updateDoc,
  query,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type {
  BabyData,
  FeedEntry,
  SleepEntry,
  DiaperEntry,
  GrowthEntry,
  SolidFoodLog,
} from '../types';
import type { DietPreference } from './solidFoodSchedule';

/**
 * Get baby's Firestore collection references
 */
function getBabyCollections(babyId: string) {
  return {
    feeds: collection(db, 'babies', babyId, 'feeds'),
    sleeps: collection(db, 'babies', babyId, 'sleeps'),
    diapers: collection(db, 'babies', babyId, 'diapers'),
    growth: collection(db, 'babies', babyId, 'growth'),
  };
}

/**
 * Convert Firestore Timestamp to Date
 */
function timestampToDate(timestamp: any): Date {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date(timestamp);
}

/**
 * Initialize empty data structure
 */
function getEmptyData(): BabyData {
  return {
    feeds: [],
    sleeps: [],
    diapers: [],
    growth: [],
    version: '1.0.0',
    createdAt: new Date(),
    lastModified: new Date(),
  };
}

/**
 * Load all data from Firestore for a baby
 */
export async function loadBabyDataFromFirestore(
  babyId: string
): Promise<BabyData> {
  try {
    const collections = getBabyCollections(babyId);

    // Load all collections in parallel
    const [feedsSnap, sleepsSnap, diapersSnap, growthSnap] = await Promise.all(
      [
        getDocs(query(collections.feeds, orderBy('timestamp', 'desc'))),
        getDocs(query(collections.sleeps, orderBy('startTime', 'desc'))),
        getDocs(query(collections.diapers, orderBy('timestamp', 'desc'))),
        getDocs(query(collections.growth, orderBy('date', 'desc'))),
      ]
    );

    const feeds: FeedEntry[] = feedsSnap.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
          timestamp: timestampToDate(doc.data().timestamp),
        } as FeedEntry)
    );

    const sleeps: SleepEntry[] = sleepsSnap.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
          startTime: timestampToDate(doc.data().startTime),
          endTime: timestampToDate(doc.data().endTime),
        } as SleepEntry)
    );

    const diapers: DiaperEntry[] = diapersSnap.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
          timestamp: timestampToDate(doc.data().timestamp),
        } as DiaperEntry)
    );

    const growth: GrowthEntry[] = growthSnap.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
          date: timestampToDate(doc.data().date),
        } as GrowthEntry)
    );

    return {
      feeds,
      sleeps,
      diapers,
      growth,
      version: '1.0.0',
      createdAt: new Date(),
      lastModified: new Date(),
    };
  } catch (error) {
    console.error('Error loading baby data from Firestore:', error);
    return getEmptyData();
  }
}

/**
 * Add a new feed entry to Firestore
 */
export async function addFeedToBaby(
  babyId: string,
  feed: Omit<FeedEntry, 'id'>
): Promise<FeedEntry> {
  try {
    const collections = getBabyCollections(babyId);
    const feedData = {
      ...feed,
      timestamp: Timestamp.fromDate(feed.timestamp),
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collections.feeds, feedData);

    return {
      id: docRef.id,
      ...feed,
    };
  } catch (error) {
    console.error('Error adding feed to baby:', error);
    throw error;
  }
}

/**
 * Add a new sleep entry to Firestore
 */
export async function addSleepToBaby(
  babyId: string,
  sleep: Omit<SleepEntry, 'id'>
): Promise<SleepEntry> {
  try {
    const collections = getBabyCollections(babyId);
    const sleepData = {
      ...sleep,
      startTime: Timestamp.fromDate(sleep.startTime),
      endTime: Timestamp.fromDate(sleep.endTime),
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collections.sleeps, sleepData);

    return {
      id: docRef.id,
      ...sleep,
    };
  } catch (error) {
    console.error('Error adding sleep to baby:', error);
    throw error;
  }
}

/**
 * Add a new diaper entry to Firestore
 */
export async function addDiaperToBaby(
  babyId: string,
  diaper: Omit<DiaperEntry, 'id'>
): Promise<DiaperEntry> {
  try {
    const collections = getBabyCollections(babyId);
    const diaperData = {
      ...diaper,
      timestamp: Timestamp.fromDate(diaper.timestamp),
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collections.diapers, diaperData);

    return {
      id: docRef.id,
      ...diaper,
    };
  } catch (error) {
    console.error('Error adding diaper to baby:', error);
    throw error;
  }
}

/**
 * Add a new growth entry to Firestore
 */
export async function addGrowthToBaby(
  babyId: string,
  growth: Omit<GrowthEntry, 'id'>
): Promise<GrowthEntry> {
  try {
    const collections = getBabyCollections(babyId);
    const growthData = {
      ...growth,
      date: Timestamp.fromDate(growth.date),
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collections.growth, growthData);

    return {
      id: docRef.id,
      ...growth,
    };
  } catch (error) {
    console.error('Error adding growth to baby:', error);
    throw error;
  }
}

/**
 * Delete an entry from Firestore
 */
export async function deleteEntryFromBaby(
  babyId: string,
  type: 'feeds' | 'sleeps' | 'diapers' | 'growth',
  id: string
): Promise<void> {
  try {
    const docRef = doc(db, 'babies', babyId, type, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting entry from baby:', error);
    throw error;
  }
}

/**
 * Update an entry in Firestore
 */
export async function updateEntryInBaby(
  babyId: string,
  type: 'feeds' | 'sleeps' | 'diapers' | 'growth',
  id: string,
  updates: any
): Promise<void> {
  try {
    const docRef = doc(db, 'babies', babyId, type, id);

    // Convert Date objects to Timestamps
    const processedUpdates = { ...updates };
    if (processedUpdates.timestamp instanceof Date) {
      processedUpdates.timestamp = Timestamp.fromDate(
        processedUpdates.timestamp
      );
    }
    if (processedUpdates.startTime instanceof Date) {
      processedUpdates.startTime = Timestamp.fromDate(
        processedUpdates.startTime
      );
    }
    if (processedUpdates.endTime instanceof Date) {
      processedUpdates.endTime = Timestamp.fromDate(processedUpdates.endTime);
    }
    if (processedUpdates.date instanceof Date) {
      processedUpdates.date = Timestamp.fromDate(processedUpdates.date);
    }

    await updateDoc(docRef, processedUpdates);
  } catch (error) {
    console.error('Error updating entry in baby:', error);
    throw error;
  }
}

// ─── Solid Food Functions ─────────────────────────────────────────────────────

/**
 * Add a solid food log entry
 */
export async function addSolidLog(
  babyId: string,
  log: Omit<SolidFoodLog, 'id'>
): Promise<SolidFoodLog> {
  const colRef = collection(db, 'babies', babyId, 'solidLogs');
  const docRef = await addDoc(colRef, {
    ...log,
    timestamp: Timestamp.fromDate(log.timestamp),
    createdAt: serverTimestamp(),
  });
  return { id: docRef.id, ...log };
}

/**
 * Get all solid food logs for a baby
 */
export async function getSolidLogs(babyId: string): Promise<SolidFoodLog[]> {
  const colRef = collection(db, 'babies', babyId, 'solidLogs');
  const snap = await getDocs(query(colRef, orderBy('timestamp', 'desc')));
  return snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    timestamp: (d.data().timestamp as Timestamp).toDate(),
  } as SolidFoodLog));
}

/**
 * Update baby's solids preferences (diet + start date) on the baby document
 */
export async function updateBabySolidsPrefs(
  babyId: string,
  prefs: { dietPreference?: DietPreference; solidsStartDate?: string }
): Promise<void> {
  const babyRef = doc(db, 'babies', babyId);
  await updateDoc(babyRef, prefs);
}
